// ================================================================
//  SwaggerEditorDialogController — manual edit of a collection's swagger
// ================================================================
//
//  Opened from the Edit Collection dialog ("Edit Swagger"). Monaco editor
//  (YAML or JSON view) over the collection's current contract, with:
//    • live local validation (parse errors marked in the editor, OpenAPI
//      shape, info.title / info.version must not change);
//    • server-side validation (same validator the backend applies on save)
//      plus a preview of the breaking operation changes, run automatically
//      once the content is locally valid;
//    • Save — enabled only when the content changed and the server
//      validated exactly the current content; never persists: it hands the
//      contract back to the caller;
//    • Cancel — discards; asks for confirmation when there are changes.
//
//  Two modes (local `editorMode`), both resolving
//  { saved: true, spec, text, format, operationsCount, changes, changesTotal }:
//    'edit'   — existing collection; the Edit Collection dialog stages the
//               contract and "Apply Changes" persists it.
//    'create' — "Create from blank" in the Create wizard, which creates the
//               collection with it.
// ================================================================

angular.module('bizmetryApp').controller('SwaggerEditorDialogController', function (
    $scope,
    $mdDialog,
    $timeout,
    $q,
    ApiCollectionService,
    dialogService,
    collection,
    editorMode,
    initialSpec
) {
    const ctrl = this;
    $scope.ctrl = ctrl;

    const MONACO_BASE = 'https://cdn.jsdelivr.net/npm/monaco-editor@0.44.0/min/vs';
    const LOCAL_DEBOUNCE_MS  = 300;
    const SERVER_DEBOUNCE_MS = 700;

    ctrl.mode     = editorMode === 'create' ? 'create' : 'edit';
    ctrl.isCreate = ctrl.mode === 'create';

    // `original` is the baseline for "has changes"; the editor opens on
    // initialSpec when given (e.g. re-editing a contract already staged in
    // the Edit Collection dialog), otherwise on the baseline itself.
    const original = ctrl.isCreate ? (initialSpec || {}) : ((collection && collection.swagger) || {});
    const startSpec = initialSpec || original;

    ctrl.collection = collection || {};
    ctrl.format     = 'yaml';            // 'yaml' | 'json'
    ctrl.isLoading  = true;
    ctrl.loadError  = null;

    ctrl.localErrors = [];               // [{ message, line }]
    ctrl.parsed      = null;             // last locally-valid object (or null)
    ctrl.dirty       = false;
    ctrl.server      = { state: 'idle' }; // idle | checking | valid | invalid | error

    let _editor       = null;
    let _text         = _serialize(startSpec, ctrl.format);
    let _localTimer   = null;
    let _serverTimer  = null;
    let _serverSeq    = 0;

    // ================================================================
    // MONACO
    // ================================================================

    function _loadMonaco() {
        if (window.monaco) { return $q.resolve(window.monaco); }
        return $q(function (resolve, reject) {
            function boot() {
                window.require.config({ paths: { vs: MONACO_BASE } });
                window.require(['vs/editor/editor.main'], function () { resolve(window.monaco); }, reject);
            }
            if (window.require && window.require.config) { boot(); return; }
            const script = document.createElement('script');
            script.src = MONACO_BASE + '/loader.js';
            script.onload = boot;
            script.onerror = function () { reject(new Error('Could not load the code editor.')); };
            document.head.appendChild(script);
        });
    }

    function _safeApply(fn) {
        if ($scope.$$phase || $scope.$root.$$phase) { fn(); } else { $scope.$apply(fn); }
    }

    _loadMonaco().then(function (monaco) {
        return $timeout(function () {
            const container = document.getElementById('swe-monaco-container');
            if (!container) { return; }
            _editor = monaco.editor.create(container, {
                value: _text,
                language: ctrl.format,
                theme: 'vs',
                automaticLayout: true,
                minimap: { enabled: false },
                fontSize: 13,
                fontFamily: "'JetBrains Mono', 'SF Mono', Consolas, monospace",
                tabSize: 2,
                insertSpaces: true,
                scrollBeyondLastLine: false,
                wordWrap: 'off',
                renderWhitespace: 'boundary',
                padding: { top: 12, bottom: 12 }
            });
            _editor.onDidChangeModelContent(function () {
                _text = _editor.getValue();
                _safeApply(_scheduleLocalValidation);
            });
            ctrl.isLoading = false;
            _validateLocal();
        }, 60);
    }).catch(function (err) {
        ctrl.isLoading = false;
        ctrl.loadError = (err && err.message) || 'Could not load the code editor.';
    });

    $scope.$on('$destroy', function () {
        if (_editor) { _editor.dispose(); _editor = null; }
        if (_localTimer)  { $timeout.cancel(_localTimer); }
        if (_serverTimer) { $timeout.cancel(_serverTimer); }
    });

    // ================================================================
    // LOCAL VALIDATION
    // ================================================================

    function _scheduleLocalValidation() {
        ctrl.server = { state: 'idle' };        // content changed: server result is stale
        if (_localTimer) { $timeout.cancel(_localTimer); }
        _localTimer = $timeout(_validateLocal, LOCAL_DEBOUNCE_MS);
    }

    function _validateLocal() {
        const errors = [];
        let obj = null;
        try {
            obj = _parse(_text, ctrl.format);
        } catch (e) {
            errors.push(_parseError(e, _text, ctrl.format));
        }
        if (obj !== null) {
            _checkShape(obj).forEach(function (m) { errors.push({ message: m, line: null }); });
        }
        ctrl.localErrors = errors;
        ctrl.parsed = errors.length === 0 ? obj : null;
        ctrl.dirty  = obj !== null && !angular.equals(obj, original);
        _setMarkers(errors);

        if (ctrl.parsed && ctrl.dirty) {
            _scheduleServerValidation();
        } else {
            ctrl.server = { state: 'idle' };
        }
    }

    function _checkShape(spec) {
        const problems = [];
        if (!spec || typeof spec !== 'object' || Array.isArray(spec)) {
            return ['The document must be an OpenAPI object.'];
        }
        const isOas3 = typeof spec.openapi === 'string' && spec.openapi.indexOf('3.') === 0;
        const isSw2  = typeof spec.swagger === 'string' && spec.swagger.indexOf('2.') === 0;
        if (!isOas3 && !isSw2) { problems.push('Not an OpenAPI 3.x or Swagger 2.x contract ("openapi" / "swagger" field).'); }
        const info = spec.info || {};
        if (!info.title) {
            problems.push('info.title is required.');
        } else if (!ctrl.isCreate && info.title !== collection.collection_name) {
            problems.push('info.title must stay "' + collection.collection_name + '".');
        }
        if (ctrl.isCreate) {
            if (info.version === undefined || info.version === null || String(info.version).trim() === '') {
                problems.push('info.version is required — it becomes the collection version.');
            }
        } else if (String(info.version) !== String(collection.api_version)) {
            problems.push('info.version must stay "' + collection.api_version + '" — create a new collection for a new version.');
        }
        if (!spec.paths || typeof spec.paths !== 'object' || Object.keys(spec.paths).length === 0) {
            problems.push('The contract has no paths.');
        } else {
            Object.keys(spec.paths).forEach(function (p) {
                if (p.charAt(0) !== '/') { problems.push('Path "' + p + '" must start with "/".'); }
            });
        }
        return problems;
    }

    function _setMarkers(errors) {
        if (!_editor || !window.monaco) { return; }
        const model = _editor.getModel();
        const markers = errors.filter(function (e) { return e.line; }).map(function (e) {
            const line = Math.min(e.line, model.getLineCount());
            return {
                severity: window.monaco.MarkerSeverity.Error,
                message: e.message,
                startLineNumber: line,
                startColumn: e.column || 1,
                endLineNumber: line,
                endColumn: model.getLineMaxColumn(line)
            };
        });
        window.monaco.editor.setModelMarkers(model, 'swagger-editor', markers);
    }

    ctrl.revealLine = function (line) {
        if (!_editor || !line) { return; }
        _editor.revealLineInCenter(line);
        _editor.setPosition({ lineNumber: line, column: 1 });
        _editor.focus();
    };

    // ================================================================
    // SERVER VALIDATION
    // ================================================================

    function _scheduleServerValidation() {
        if (_serverTimer) { $timeout.cancel(_serverTimer); }
        _serverTimer = $timeout(ctrl.validateNow, SERVER_DEBOUNCE_MS);
    }

    /** Also bound to the "Validate" button. */
    ctrl.validateNow = function () {
        if (!ctrl.parsed) { return; }
        const seq = ++_serverSeq;
        const text = _text;
        ctrl.server = { state: 'checking' };
        const call = ctrl.isCreate
            ? ApiCollectionService.validateNewSwagger(ctrl.parsed)
            : ApiCollectionService.validateSwagger(collection.collection_id, ctrl.parsed);
        call
            .then(function (res) {
                if (seq !== _serverSeq) { return; }
                ctrl.server = {
                    state: res.valid ? 'valid' : 'invalid',
                    message: res.message,
                    operationsCount: res.operations_count,
                    changes: (res.changes || []).map(_describeChange),
                    changesTotal: res.changes_total || 0,
                    // Fields new in existing operations: not breaking, but Biz Events using
                    // those operations get IntelliSense over their unmapped targets.
                    added: (res.added_fields || []).map(_describeChange),
                    addedTotal: res.added_total || 0,
                    // PUBLISHED collections are saved as a new DRAFT version (server-computed bump).
                    nextVersion: res.next_version || null,
                    // Biz event versions this edit recomputes; null = couldn't be determined.
                    affectedBizEvents: Array.isArray(res.affected_biz_events) ? res.affected_biz_events : null,
                    forText: text
                };
            })
            .catch(function (err) {
                if (seq !== _serverSeq) { return; }
                ctrl.server = {
                    state: 'error',
                    message: (err && err.data && err.data.message) || 'The server could not validate the contract.'
                };
            });
    };

    function _describeChange(c) {
        const op = c.operationName || c.operationId;
        switch (c.changeType) {
            case 'OPERATION_REMOVED':  return { icon: 'remove_circle', kind: 'removed', text: 'Operation ' + op + ' removed' };
            case 'OPERATION_RENAMED':  return { icon: 'drive_file_rename_outline', kind: 'renamed', text: 'Operation ' + op + ' → ' + c.newOperationName };
            case 'OPERATION_MOVED':    return { icon: 'moving', kind: 'renamed', text: 'Operation ' + op + ' moved → ' + c.newMethod + ' ' + c.newPath };
            case 'FIELD_REMOVED':      return { icon: 'remove', kind: 'removed', text: op + ' · ' + _kind(c.sourceType) + ' ' + c.fieldPath + ' removed' };
            case 'FIELD_TYPE_CHANGED': return { icon: 'swap_horiz', kind: 'retyped', text: op + ' · ' + _kind(c.sourceType) + ' ' + c.fieldPath + ' ' + c.oldType + ' → ' + c.newType };
            case 'FIELD_ADDED':        return { icon: 'add', kind: 'added', text: op + ' · ' + _kind(c.sourceType) + ' ' + c.fieldPath + (c.newType ? ' (' + c.newType + ')' : '') };
            default:                   return { icon: 'change_circle', kind: 'other', text: op + ' changed' };
        }
    }

    function _kind(sourceType) {
        return ({ REQUEST_FIELD: 'request', RESPONSE_FIELD: 'response', URI_PARAM: 'path param',
                  QUERY_PARAM: 'query param', REQUEST_HEADER: 'header' })[sourceType] || '';
    }

    // ================================================================
    // FORMAT (YAML / JSON view)
    // ================================================================

    ctrl.setFormat = function (format) {
        if (format === ctrl.format) { return; }
        let obj;
        try { obj = _parse(_text, ctrl.format); } catch (e) { obj = null; }
        if (obj === null) {
            ctrl.formatNotice = 'Fix the syntax errors before switching to ' + format.toUpperCase() + '.';
            $timeout(function () { ctrl.formatNotice = null; }, 3500);
            return;
        }
        ctrl.formatNotice = null;
        ctrl.format = format;
        _text = _serialize(obj, format);
        if (_editor && window.monaco) {
            window.monaco.editor.setModelLanguage(_editor.getModel(), format);
            _editor.setValue(_text);      // triggers validation through the change listener
        }
    };

    // ================================================================
    // SAVE / CANCEL
    // ================================================================

    ctrl.canSave = function () {
        return ctrl.dirty && ctrl.localErrors.length === 0
            && ctrl.server.state === 'valid' && ctrl.server.forText === _text;
    };

    /**
     * Save only confirms the edited contract and hands it back to the caller —
     * nothing is persisted here. Edit mode: the Edit Collection dialog stages it
     * and "Apply Changes" saves it. Create mode: the Create wizard uses it.
     */
    ctrl.save = function () {
        if (!ctrl.canSave()) { return; }
        $mdDialog.hide({
            saved: true,
            spec: ctrl.parsed,
            text: _text,
            format: ctrl.format,
            operationsCount: ctrl.server.operationsCount,
            changes: ctrl.server.changes || [],
            changesTotal: ctrl.server.changesTotal || 0,
            nextVersion: ctrl.server.nextVersion || null,
            affectedBizEvents: ctrl.server.affectedBizEvents
        });
    };

    ctrl.cancel = function () {
        if (!ctrl.dirty) { $mdDialog.cancel(); return; }
        dialogService.showDecisionDialog(
            'Discard Changes',
            ctrl.isCreate
                ? 'The contract you wrote will be lost. Are you sure you want to close the editor?'
                : 'You have unsaved swagger changes. Are you sure you want to close without saving?'
        ).then(function (decision) {
            if (decision === 'proceed') { $mdDialog.cancel(); }
        });
    };

    // ================================================================
    // HELPERS
    // ================================================================

    function _serialize(obj, format) {
        if (format === 'json') { return JSON.stringify(obj, null, 2) + '\n'; }
        return window.jsyaml.dump(obj, { lineWidth: -1, noRefs: true });
    }

    function _parse(text, format) {
        if (format === 'json') { return JSON.parse(text); }
        const obj = window.jsyaml.load(text);
        return obj === undefined ? null : obj;
    }

    function _parseError(e, text, format) {
        if (format === 'yaml' && e && e.mark) {
            return { message: 'YAML: ' + (e.reason || e.message), line: e.mark.line + 1, column: e.mark.column + 1 };
        }
        const msg = (e && e.message) || 'Invalid document';
        let line = null;
        const lineMatch = /line (\d+)/i.exec(msg);
        const posMatch  = /position (\d+)/i.exec(msg);
        if (lineMatch) {
            line = parseInt(lineMatch[1], 10);
        } else if (posMatch) {
            line = text.slice(0, parseInt(posMatch[1], 10)).split('\n').length;
        }
        return { message: (format === 'json' ? 'JSON: ' : '') + msg, line: line };
    }
});
